import React, { useCallback } from 'react';
import { Checkbox, TextField, Typography } from '@material-ui/core';
import Autocomplete from '@mui/material/Autocomplete';
import { RootState, useDispatch, useSelector } from 'react-redux';
import { getSearchParameters } from 'src/products/core-prospect/search-pages/selectors';
import { Field, FieldType } from './types';
import useStyles from './styles';
import { changeSearchParameter } from 'src/products/core-prospect/redux/query/actions';

type DealCloudFormProps = {
  field: Field;
};

const getFilterValue = (field_name: string) => (state: RootState) => {
  const search_parameters = getSearchParameters(state);
  return search_parameters.find((search_parameter) => {
    return (
      search_parameter.entity_name === 'dealV2' &&
      search_parameter.field_name === field_name
    );
  })?.attributes?.value;
};

const FilterComponent: React.FC<DealCloudFormProps> = ({ field }) => {
  const classes = useStyles();

  const fieldValue = useSelector(getFilterValue(field.name));

  let selectedValue: { id: string; name: string } | null = null;

  if (field.options && field.searchOptions.type === FieldType.SELECT) {
    selectedValue =
      field.options.find((value) => value.id === fieldValue) || null;
  }

  const dispatch = useDispatch();

  const onChange = useCallback((field_name: string, value: any) => {
    dispatch(
      changeSearchParameter(
        {
          id: 39,
          entity: 'dealV2',
          filter_set_id: 13,
          filter_type: 'DealFilterV2',
          field_name,
        },
        value
      )
    );
  }, []);

  const toggleCheckbox = (value: string) => () => {
    if (Array.isArray(fieldValue)) {
      if (fieldValue.includes(value)) {
        onChange(
          field.name,
          fieldValue.filter((name) => name !== value)
        );
      } else {
        onChange(field.name, [...fieldValue, value]);
      }
    } else {
      onChange(field.name, [value]);
    }
  };

  return (
    <div>
      <Typography variant='body1' className={classes.title}>
        {field.label}
      </Typography>
      {(() => {
        switch (field.searchOptions.type) {
          case FieldType.TEXT:
            return (
              <TextField
                value={fieldValue ?? ''}
                placeholder={field.label}
                onChange={(e) => onChange(field.name, e.target.value)}
              />
            );
          case FieldType.NUMBER:
            return (
              <TextField
                type='number'
                variant='outlined'
                value={fieldValue ?? ''}
                placeholder={field.label}
                onChange={(e) => onChange(field.name, Number(e.target.value))}
              />
            );
          case FieldType.CHECKBOX:
            return (
              <div className={classes.stagesContainer}>
                {field.options?.map((option) => {
                  const selected = Array.isArray(fieldValue)
                    ? fieldValue.includes(option.id)
                    : false;
                  return (
                    <div
                      key={option.id}
                      className={
                        selected
                          ? classes.selectedCheckbox
                          : classes.unselectedCheckbox
                      }
                      onClick={toggleCheckbox(option.id)}
                      title={option.name}
                    >
                      <Checkbox
                        color='primary'
                        disableRipple
                        style={{ height: 30, width: 34 }}
                        checked={selected}
                      />
                      <Typography
                        variant='body1'
                        className={classes.checkboxText}
                      >
                        {option.name}
                      </Typography>
                    </div>
                  );
                })}
              </div>
            );
          case FieldType.SELECT:
            return (
              <Autocomplete
                value={selectedValue}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                    size='small'
                    placeholder='Select'
                    style={{ backgroundColor: 'white' }}
                  />
                )}
                onChange={(_, value) =>
                  onChange(field.name, value ? value.id : '')
                }
                isOptionEqualToValue={(opt, value) => opt.id === value.id}
                getOptionLabel={(opt) => opt.name}
                options={field.options ?? []}
              />
            );
          default:
            return;
        }
      })()}
    </div>
  );
};

export default FilterComponent;
